import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

type Leader = {
  id: string;
  name: string;
  role: string;
  slug: string;
  image_url: string | null;
  image_position: string;
  tagline: string | null;
  intro: string | null;
  content: string | null;
  city: string | null;
  experience: string | null;
  sort_order: number;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyForm = {
  name: "",
  role: "",
  slug: "",
  tagline: "",
  intro: "",
  content: "",
  city: "",
  experience: "",
  image_position: "center 25%",
  sort_order: 0,
};

const LeadersAdmin = () => {
  const { toast } = useToast();
  const { signOut, user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Leader | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { data: leaders = [], isLoading } = useQuery({
    queryKey: ["admin-leaders"],
    queryFn: async () => {
      const { data, error } = await supabase.from("leaders").select("*").order("sort_order");
      if (error) throw error;
      return data as Leader[];
    },
  });

  const uploadImage = async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `leader-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("team-images").upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from("team-images").getPublicUrl(path);
    return data.publicUrl;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let imageUrl = editing?.image_url || null;
      if (imageFile) imageUrl = await uploadImage(imageFile);

      const slug = (form.slug.trim() || slugify(form.name));
      const payload = {
        name: form.name.trim(),
        role: form.role.trim(),
        slug,
        tagline: form.tagline.trim() || null,
        intro: form.intro.trim() || null,
        content: form.content || null,
        city: form.city.trim() || null,
        experience: form.experience.trim() || null,
        image_position: form.image_position.trim() || "center 25%",
        sort_order: form.sort_order,
        image_url: imageUrl,
      };

      if (editing) {
        const { error } = await supabase.from("leaders").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("leaders").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: editing ? "Leader updated" : "Leader added" });
      queryClient.invalidateQueries({ queryKey: ["admin-leaders"] });
      queryClient.invalidateQueries({ queryKey: ["leaders"] });
      queryClient.invalidateQueries({ queryKey: ["leader"] });
      resetForm();
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("leaders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Leader deleted" });
      queryClient.invalidateQueries({ queryKey: ["admin-leaders"] });
      queryClient.invalidateQueries({ queryKey: ["leaders"] });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const resetForm = () => {
    setEditing(null);
    setCreating(false);
    setImageFile(null);
    setForm(emptyForm);
  };

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setImageFile(null);
    setCreating(true);
  };

  const startEdit = (l: Leader) => {
    setEditing(l);
    setCreating(true);
    setForm({
      name: l.name,
      role: l.role,
      slug: l.slug,
      tagline: l.tagline || "",
      intro: l.intro || "",
      content: l.content || "",
      city: l.city || "",
      experience: l.experience || "",
      image_position: l.image_position,
      sort_order: l.sort_order,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-bold text-foreground">Manage Leaders</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {!creating && (
          <>
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">{leaders.length} leaders</p>
              <Button onClick={startCreate}>
                <Plus className="w-4 h-4 mr-1" /> Add Leader
              </Button>
            </div>

            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading…</div>
            ) : (
              <div className="space-y-3">
                {leaders.map((l) => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-4 bg-card border border-border rounded-lg p-4"
                  >
                    {l.image_url && (
                      <img
                        src={l.image_url}
                        alt={l.name}
                        style={{ objectPosition: l.image_position }}
                        className="w-14 h-14 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{l.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {l.role} · /{l.slug} · order: {l.sort_order}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(l)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm("Delete this leader?")) deleteMutation.mutate(l.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {creating && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">{editing ? "Edit Leader" : "New Leader"}</h2>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 max-w-3xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((f) => ({
                        ...f,
                        name,
                        slug: !editing && (!f.slug || f.slug === slugify(f.name)) ? slugify(name) : f.slug,
                      }));
                    }}
                    required
                    maxLength={200}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Input
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    required
                    maxLength={200}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL Slug *</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                    placeholder="e.g. jane-doe"
                    required
                    maxLength={200}
                  />
                  <p className="text-xs text-muted-foreground">Page URL: /leaders/{form.slug || "…"}</p>
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Short subtitle shown under the name"
                  maxLength={300}
                />
              </div>

              <div className="space-y-2">
                <Label>Intro Paragraph</Label>
                <Textarea
                  value={form.intro}
                  onChange={(e) => setForm({ ...form, intro: e.target.value })}
                  placeholder="One-paragraph introduction shown in the hero section"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label>Experience Summary</Label>
                  <Input
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    maxLength={300}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Image Position</Label>
                <Input
                  value={form.image_position}
                  onChange={(e) => setForm({ ...form, image_position: e.target.value })}
                  placeholder="center 25%"
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">CSS object-position (e.g. center 20%)</p>
              </div>

              <div className="space-y-2">
                <Label>Photo {!editing && "*"}</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-muted text-sm">
                    <Upload className="w-4 h-4" />
                    {imageFile ? imageFile.name : "Choose file"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {editing?.image_url && !imageFile && (
                    <img
                      src={editing.image_url}
                      alt=""
                      style={{ objectPosition: editing.image_position }}
                      className="w-14 h-14 object-cover rounded"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Full Profile Content (Markdown)</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder={"## Section Heading\n\nParagraph text…\n\n- Bullet item\n- Another item"}
                />
                <p className="text-xs text-muted-foreground">
                  Supports Markdown: <code>## Heading</code>, <code>**bold**</code>, <code>- list item</code>.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving…" : editing ? "Update Leader" : "Create Leader"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default LeadersAdmin;
